import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { 
  Plus, Trash2, GripVertical, Image, Video, Type, FileText, 
  ChevronUp, ChevronDown, Youtube
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ContentBlock {
  id: string;
  type: 'heading' | 'paragraph' | 'image' | 'video' | 'list' | 'quote';
  content: string;
  metadata?: {
    level?: 1 | 2 | 3; // For headings
    alt?: string; // For images
    attribution?: string; // For images
    url?: string; // For images/videos
    videoId?: string; // For YouTube
    items?: string[]; // For lists
  };
}

export interface ContentSection {
  id: string;
  title: string;
  blocks: ContentBlock[];
}

interface RichBlockEditorProps {
  blocks: ContentBlock[];
  onChange: (blocks: ContentBlock[]) => void;
  className?: string;
  placeholder?: string;
}

const generateId = () => Math.random().toString(36).substr(2, 9);

const BLOCK_TYPES = [
  { type: 'heading', icon: Type, label: 'Heading' },
  { type: 'paragraph', icon: FileText, label: 'Paragraph' },
  { type: 'image', icon: Image, label: 'Image' },
  { type: 'video', icon: Youtube, label: 'YouTube Video' },
] as const;

export function RichBlockEditor({ 
  blocks, 
  onChange, 
  className,
  placeholder = 'Add content blocks...'
}: RichBlockEditorProps) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const addBlock = (type: ContentBlock['type']) => {
    const newBlock: ContentBlock = {
      id: generateId(),
      type,
      content: '',
      metadata: type === 'heading' ? { level: 2 } : {},
    };
    onChange([...blocks, newBlock]);
  };

  const updateBlock = (index: number, updates: Partial<ContentBlock>) => {
    const updated = [...blocks];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const updateBlockMetadata = (index: number, metadata: Partial<ContentBlock['metadata']>) => {
    const updated = [...blocks];
    updated[index] = { 
      ...updated[index], 
      metadata: { ...updated[index].metadata, ...metadata } 
    };
    onChange(updated);
  };

  const removeBlock = (index: number) => {
    onChange(blocks.filter((_, i) => i !== index));
  };

  const moveBlock = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= blocks.length) return;
    
    const updated = [...blocks];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onChange(updated);
  };

  const extractYouTubeId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const renderBlockEditor = (block: ContentBlock, index: number) => {
    switch (block.type) {
      case 'heading':
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <select
                value={block.metadata?.level || 2}
                onChange={(e) => updateBlockMetadata(index, { level: parseInt(e.target.value) as 1 | 2 | 3 })}
                className="text-sm border rounded px-2 py-1"
              >
                <option value={1}>H1 - Main Heading</option>
                <option value={2}>H2 - Section Heading</option>
                <option value={3}>H3 - Subheading</option>
              </select>
            </div>
            <Input
              value={block.content}
              onChange={(e) => updateBlock(index, { content: e.target.value })}
              placeholder="Enter heading text..."
              className="font-semibold"
            />
          </div>
        );

      case 'paragraph':
        return (
          <Textarea
            value={block.content}
            onChange={(e) => updateBlock(index, { content: e.target.value })}
            placeholder="Enter paragraph text..."
            rows={4}
            className="resize-y"
          />
        );

      case 'image':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Image URL</Label>
              <Input
                value={block.metadata?.url || ''}
                onChange={(e) => updateBlockMetadata(index, { url: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
            </div>
            {block.metadata?.url && (
              <div className="relative aspect-video w-full max-w-md rounded overflow-hidden bg-muted">
                <img 
                  src={block.metadata.url} 
                  alt={block.metadata?.alt || ''} 
                  className="object-cover w-full h-full"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/placeholder.svg';
                  }}
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Alt Text</Label>
                <Input
                  value={block.metadata?.alt || ''}
                  onChange={(e) => updateBlockMetadata(index, { alt: e.target.value })}
                  placeholder="Describe the image..."
                />
              </div>
              <div>
                <Label className="text-xs">Attribution (optional)</Label>
                <Input
                  value={block.metadata?.attribution || ''}
                  onChange={(e) => updateBlockMetadata(index, { attribution: e.target.value })}
                  placeholder="Photo by..."
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Caption (optional)</Label>
              <Input
                value={block.content}
                onChange={(e) => updateBlock(index, { content: e.target.value })}
                placeholder="Image caption..."
              />
            </div>
          </div>
        );

      case 'video':
        const videoId = block.metadata?.videoId || extractYouTubeId(block.content);
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">YouTube URL or Video ID</Label>
              <Input
                value={block.content}
                onChange={(e) => {
                  const url = e.target.value;
                  const id = extractYouTubeId(url);
                  updateBlock(index, { 
                    content: url,
                    metadata: { ...block.metadata, videoId: id || undefined }
                  });
                }}
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </div>
            {videoId && (
              <div className="aspect-video w-full max-w-md rounded overflow-hidden bg-muted">
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}`}
                  title="YouTube video"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
          </div>
        );

      default:
        return (
          <Textarea
            value={block.content}
            onChange={(e) => updateBlock(index, { content: e.target.value })}
            placeholder="Enter content..."
            rows={3}
          />
        );
    }
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Block List */}
      {blocks.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
          <p className="mb-4">{placeholder}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {blocks.map((block, index) => (
            <Card 
              key={block.id} 
              className={cn(
                'p-4 transition-opacity',
                draggedIndex === index && 'opacity-50'
              )}
            >
              <div className="flex items-start gap-3">
                {/* Drag Handle */}
                <div className="flex flex-col items-center gap-1 pt-1">
                  <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => moveBlock(index, 'up')}
                    disabled={index === 0}
                  >
                    <ChevronUp className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => moveBlock(index, 'down')}
                    disabled={index === blocks.length - 1}
                  >
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </div>

                {/* Block Content */}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground uppercase">
                      {block.type}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-destructive hover:text-destructive"
                      onClick={() => removeBlock(index)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  {renderBlockEditor(block, index)}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add Block Buttons */}
      <div className="flex flex-wrap gap-2 pt-2 border-t">
        <span className="text-xs text-muted-foreground self-center mr-2">Add:</span>
        {BLOCK_TYPES.map(({ type, icon: Icon, label }) => (
          <Button
            key={type}
            variant="outline"
            size="sm"
            onClick={() => addBlock(type)}
            className="h-8"
          >
            <Icon className="h-3 w-3 mr-1" />
            {label}
          </Button>
        ))}
      </div>
    </div>
  );
}
